// lineageSeed — whiteboard.restore を跨ぐ「1本のタイムラプス」の系譜 seed
// 導出 (Slice 3 §4.5-2、past-canvas-new-document-2026-07-05.md)。
//
// `whiteboard.restore` は stroke log に焼き込まれる**系譜マーカー** (§4.5-1):
//   - **ドキュメント境界**: 全消し (`whiteboard.clear` かつ layerId 無し) と
//     同様に「現在の文書」の開始点になる (seed の遡りはここで打ち切る)。
//   - **系譜リンク**: `restoredFromSeq` (= 復元種 snapshot に焼き込まれた最大
//     broadcast seq) から復元元ドキュメントの seq 範囲
//     `(復元元の開始境界, restoredFromSeq]` を導出できる。復元元がさらに
//     restore で始まる文書なら再帰的に遡る (深さ上限 {@link MAX_LINEAGE_DEPTH})。
//
// 意味論 (§4.5 コード検証で確定):
//   - **restore = 種差し替え**: マーカー時点でキャンバス全体が復元種 (= 復元元
//     最終 snapshot) に置き換わる。replay では「全レイヤ + 背景をリセット →
//     系譜範囲を root→leaf 順に無音再適用」で bit-perfect に土台を再構築する
//     (replay はサーバ SnapshotEngine と同じイベント適用経路なので、同じ列を
//     再適用した状態は snapshot とビット一致する)。raster snapshot を読む
//     経路は作らない (`snapshot` presigned URL は期限切れ前提で使わない)。
//   - **系譜連結の上端は `restoredFromSeq` (§4.5-3)**: 復元種 = 復元元の最終
//     snapshot なので、snapshot 後〜全消し間の描画は復元されていない =
//     タイムラプスに含めてはいけない。
//   - **飛ばされた中間文書のイベントは適用しない**: 系譜範囲外は土台に混ぜない
//     (computeSeedRange の「開始点より前の別文書の描画は混ざらない」原則の拡張)。
//   - **チェーンは重ねて適用する** (範囲間でリセットしない): 復元で始まった
//     文書のイベント列は「復元種の上に描かれた」ものなので、root の範囲を
//     適用した状態の上に子の範囲を順に重ねる。リセットは系譜再構築の入口
//     (= 直前文書の状態を捨てるとき) の 1 回だけ。
//   - **gap tolerate**: 系譜範囲のセグメントが purge / 欠損で無くても例外に
//     しない。欠けた分は土台から抜ける (既存の T0 gap 方針の拡張)。
//
// このモジュールは pure (fetch / engine なし)。`segmentMerge.computeSeedRange`
// (単一連続 SeqRange API) は互換のため温存し、こちらは**追加**の一般化 API。

import type { StrokeLogEvent } from '@oekaki/protocol';
import type { SeqRange } from './segmentMerge.js';

/**
 * 系譜チェーンの再帰深さ上限。超過分は打ち切り = 最深部の土台が欠けた状態で
 * graceful degradation (例外にしない。§4.3 キャップ purge と同じ精神)。
 */
export const MAX_LINEAGE_DEPTH = 10;

/** `whiteboard.restore` ログイベントの系譜導出に必要な最小 shape。 */
export interface RestoreMarkerLike {
  readonly seq: number;
  readonly restoredFromSeq: number;
  /** baked 判定用 (省略可: 判定しない)。 */
  readonly restoredFromSessionId?: string;
}

/**
 * restore マーカー 1 個の土台再構築計画: **全レイヤ + 背景をリセット**した後、
 * `ranges` を root→leaf 順にそのまま (リセットを挟まず) 無音適用する。
 * `ranges` は互いに素で昇順 (チェーンの各文書範囲は seq 上重ならない)。
 * 系譜が完全に purge 済みなら空 (= リセットのみ → 白紙の上に続きが乗る)。
 */
export interface RestoreRebuild {
  readonly ranges: readonly SeqRange[];
}

/**
 * 可視範囲の手前の無音 seed 計画 (`computeSeedRange` の系譜対応版)。
 *   - `rebuild !== null`: 現在の文書が restore マーカーで始まる → まず
 *     リセット + 系譜範囲を適用し、その後 `prelude` を適用する。
 *   - `rebuild === null`: 従来どおり (最後の全消し or 文書先頭から) `prelude`
 *     のみ = `computeSeedRange` と同一の範囲。
 *   - `leadingRun !== null`: 可視範囲の**先頭がそれ自体、文書境界**(全消し
 *     自身 / ログ先頭) で始まるケース (fix/timelapse-background Bug A)。
 *     この場合 `rebuild`/`prelude` は使わない (別文書の描画を混ぜてはいけない
 *     — `collectFinalDocumentRanges` が境界イベント自身を範囲の先頭に含める
 *     ため、旧来の「fromSeq - 1 から遡る」ロジックのままだと 1 つ前の文書が
 *     丸ごと土台に混ざってしまう)。代わりに、境界直後〜最初の描画イベント
 *     直前までの非描画イベント (`whiteboard.clear` / `whiteboard.background-set`)
 *     だけを無音の土台にし、frame 0 を「描き始める直前の状態」にする —
 *     プレビュー始点 (`previewFrame.ts`、白紙 replay) と一致させる狙い。
 */
export interface LineageSeedPlan {
  readonly rebuild: RestoreRebuild | null;
  /**
   * `rebuild` が非 null のとき、その系譜を発生させたマーカーの
   * `restoredFromSeq` (= `resetForRestore` の背景色ターゲット、
   * `backgroundAt(restoredFromSeq)` で求める、Bug B/C 修正)。`rebuild` が
   * null なら無関係、常に null。
   */
  readonly rebuildRestoredFromSeq: number | null;
  /** マーカー/全消しの後〜可視開始直前の「現在の文書」土台。空なら null。 */
  readonly prelude: SeqRange | null;
  /** 可視範囲の先頭が文書境界そのもので始まるときの境界直後の非描画イベント。 */
  readonly leadingRun: SeqRange | null;
}

// ─── 内部: ドキュメント開始境界の走査 ──────────────────────────────────────

export type DocumentStart =
  /** 最後の全消し (範囲にはこの wipe 自体を含める = computeSeedRange と同一)。 */
  | { readonly kind: 'clear'; readonly seq: number }
  /** 最後の restore マーカー (範囲はマーカーの次から = マーカー自体は適用しない)。 */
  | { readonly kind: 'restore'; readonly marker: RestoreMarkerLike }
  /** 境界なし = ログ先頭 (seq は最初のイベント)。 */
  | { readonly kind: 'head'; readonly seq: number };

/**
 * `maxSeq` (inclusive) 以前で最後のドキュメント境界を探す。イベントが 1 件も
 * 無ければ null (= その seq 以前のログが手元に無い / 空)。
 * `events` は昇順 seq 前提 (merge 済み)。
 */
export const findDocumentStart = (
  events: readonly StrokeLogEvent[],
  maxSeq: number,
): DocumentStart | null => {
  let head: number | null = null;
  let hit: DocumentStart | null = null;
  for (const ev of events) {
    if (ev.seq > maxSeq) break; // ascending
    if (head === null) head = ev.seq;
    if (ev.t === 'whiteboard.clear' && ev.layerId === undefined) {
      hit = { kind: 'clear', seq: ev.seq };
    } else if (ev.t === 'whiteboard.restore') {
      hit = { kind: 'restore', marker: ev };
    }
  }
  if (hit !== null) return hit;
  if (head !== null) return { kind: 'head', seq: head };
  return null;
};

/**
 * 系譜チェーンの範囲を root→leaf 順に `out` へ積む (再帰)。
 *
 * `conservativeHead = true` (DL 計画用): 境界が見つからない (= その seq 以前の
 * イベントが未取得かもしれない) とき、下端 0 の保守的範囲 `[0, r]` を要求に
 * 含める — 追加 DL 後の re-merge / 再走査で真の境界に収束する
 * (`strokeLogTimeline` の反復ループ参照)。false (replay 用) では手元の
 * イベント列がすべてなので、ログ先頭 = 最初のイベント seq を下端にする。
 */
const isBakedSource = (
  marker: RestoreMarkerLike,
  bakedSessionIds: ReadonlySet<string> | undefined,
): boolean =>
  bakedSessionIds !== undefined &&
  marker.restoredFromSessionId !== undefined &&
  bakedSessionIds.has(marker.restoredFromSessionId);

const collectChainRanges = (
  events: readonly StrokeLogEvent[],
  marker: RestoreMarkerLike,
  depth: number,
  conservativeHead: boolean,
  out: SeqRange[],
  bakedSessionIds?: ReadonlySet<string>,
): void => {
  const r = marker.restoredFromSeq;
  // 復元種 seq は必ずマーカーより前 (壊れたログの循環ガード)。
  if (r >= marker.seq) return;
  // 復元元が baked (`kind: 'excluded'`) のセッションなら、そこで系譜は止まる
  // (baked セッションは自前の絵ログを持たない = protocol `resolveLineage` の
  // baked 規則と lockstep)。
  if (isBakedSource(marker, bakedSessionIds)) return;
  const boundary = findDocumentStart(events, r);
  if (boundary === null) {
    // r 以前のイベントが手元に無い: DL 計画なら「全部取れ」、replay なら
    // 土台欠落として無視 (gap tolerate)。
    if (conservativeHead) out.push({ fromSeq: 0, toSeq: r });
    return;
  }
  if (boundary.kind === 'restore') {
    if (depth > 1) {
      collectChainRanges(
        events,
        boundary.marker,
        depth - 1,
        conservativeHead,
        out,
        bakedSessionIds,
      );
    }
    // 深さ上限到達時はここで打ち切り = より深い土台が欠けた graceful degradation。
    const fromSeq = boundary.marker.seq + 1;
    if (fromSeq <= r) out.push({ fromSeq, toSeq: r });
    return;
  }
  if (boundary.kind === 'head' && conservativeHead) {
    // 手元の最初のイベントより前に境界が潜んでいる可能性 → 保守的に下端 0。
    out.push({ fromSeq: 0, toSeq: r });
    return;
  }
  // clear は wipe 自体を含める (白紙化 → 以降で土台を再蓄積、computeSeedRange と同一)。
  out.push({ fromSeq: boundary.seq, toSeq: r });
};

/** `DocumentStart` の位置 seq (restore は `marker.seq`、他は `seq`)。 */
const documentStartSeq = (ds: DocumentStart): number =>
  ds.kind === 'restore' ? ds.marker.seq : ds.seq;

/** restore マーカー 1 個の再構築計画 (replay 用 = 手元イベント基準の下端)。 */
export const buildRestoreRebuild = (
  events: readonly StrokeLogEvent[],
  marker: RestoreMarkerLike,
  maxDepth: number = MAX_LINEAGE_DEPTH,
  bakedSessionIds?: ReadonlySet<string>,
): RestoreRebuild => {
  const ranges: SeqRange[] = [];
  collectChainRanges(events, marker, maxDepth, false, ranges, bakedSessionIds);
  return { ranges };
};

/**
 * 可視範囲手前の無音 seed 計画を導出する (`computeSeedRange` の一般化)。
 * restore が絡まないログでは `computeSeedRange` と完全に同じ範囲を返す
 * (`rebuild: null` + 同一 `prelude`) — 既存挙動のバイト一致を保つ。
 *
 * Bug A 修正 (fix/timelapse-background): 可視範囲の**先頭がそれ自体、文書
 * 境界**(全消し自身 / ログ先頭) なら、先に `leadingRun` 分岐で処理し、
 * `rebuild`/`prelude` は使わない (詳細は {@link LineageSeedPlan} 参照)。
 */
export const computeLineageSeedPlan = (
  events: readonly StrokeLogEvent[],
  visible: SeqRange | undefined,
): LineageSeedPlan | null => {
  if (visible === undefined) return null;
  const atStart = findDocumentStart(events, visible.fromSeq);
  if (atStart !== null && documentStartSeq(atStart) === visible.fromSeq) {
    const leadingRun = findLeadingNonDrawingRun(events, visible);
    // 境界の直後に非描画イベントも無い (= 先頭イベントが既に描画イベント) なら
    // 土台自体が不要 — `computeSeedRange` の「開始点が文書先頭 = null」と同じ
    // 形に揃える (呼び出し側は null / 全 null オブジェクトのどちらでも同じに
    // 振る舞うが、既存呼び出しの「seed なし」判定と揃えておく)。
    return leadingRun === null
      ? null
      : { rebuild: null, rebuildRestoredFromSeq: null, prelude: null, leadingRun };
  }
  const boundary = findDocumentStart(events, visible.fromSeq - 1);
  if (boundary === null) return null; // 開始点が文書先頭 = 土台なし
  if (boundary.kind === 'restore') {
    const m = boundary.marker.seq;
    const prelude =
      m + 1 <= visible.fromSeq - 1 ? { fromSeq: m + 1, toSeq: visible.fromSeq - 1 } : null;
    return {
      rebuild: buildRestoreRebuild(events, boundary.marker),
      rebuildRestoredFromSeq: boundary.marker.restoredFromSeq,
      prelude,
      leadingRun: null,
    };
  }
  // clear / head — computeSeedRange と同一の単一範囲。
  return {
    rebuild: null,
    rebuildRestoredFromSeq: null,
    prelude: { fromSeq: boundary.seq, toSeq: visible.fromSeq - 1 },
    leadingRun: null,
  };
};

/**
 * 可視範囲の先頭が文書境界そのもの (全消し自身 / ログ先頭) で始まるときの
 * 「境界〜最初の描画イベント (`stroke.start` / `whiteboard.imagepaste`) 直前
 * まで」の非描画イベント (`whiteboard.clear` / `whiteboard.background-set`)
 * の並び。`range` の外へは探索しない (系譜連結ドメインの離散範囲リストでは
 * 先頭範囲にだけ関わる — 2 本目以降の範囲には無関係)。先頭イベントが既に
 * 描画イベントなら null (土台なし)。
 */
export const findLeadingNonDrawingRun = (
  events: readonly StrokeLogEvent[],
  range: SeqRange,
): SeqRange | null => {
  let end: number | null = null;
  for (const ev of events) {
    if (ev.seq < range.fromSeq) continue;
    if (ev.seq > range.toSeq) break;
    if (ev.t === 'whiteboard.clear' || ev.t === 'whiteboard.background-set') {
      end = ev.seq;
      continue;
    }
    break; // 最初の描画イベント (or 迷い込んだ restore) で走査を打ち切る
  }
  return end === null ? null : { fromSeq: range.fromSeq, toSeq: end };
};

/**
 * 可視範囲**内**に現れる restore マーカー (スライダーが境界を跨ぐケース、
 * §4.5-2 意味論 3) の再構築計画を seq → rebuild の Map で返す。replay の
 * 可視ループがマーカーに当たったら「リセット + 系譜範囲を無音再適用」して
 * フレーム化を続行する。`visible` 未指定 = 全範囲再生 → 全マーカーが対象。
 *
 * `visible` は離散範囲リスト (§4.7 可視フェーズ一般化、昇順・互いに素) —
 * いずれかの範囲に入るマーカーだけが対象。系譜連結ドメイン (範囲リスト =
 * `collectFinalDocumentRanges` 由来) では restore マーカーは常に範囲**間**
 * (範囲 k の toSeq = restoredFromSeq < marker.seq < 範囲 k+1 の fromSeq) に
 * 落ちるので Map は空になり、範囲ジャンプではリセットも rebuild も起こらない
 * (= 「範囲外のイベントを適用しないだけ」)。
 */
export const collectVisibleRestoreRebuilds = (
  events: readonly StrokeLogEvent[],
  visible: readonly SeqRange[] | undefined,
): ReadonlyMap<number, RestoreRebuild> => {
  const map = new Map<number, RestoreRebuild>();
  for (const ev of events) {
    if (ev.t !== 'whiteboard.restore') continue;
    if (visible !== undefined && !visible.some((r) => ev.seq >= r.fromSeq && ev.seq <= r.toSeq)) {
      continue;
    }
    map.set(ev.seq, buildRestoreRebuild(events, ev));
  }
  return map;
};

/**
 * セグメント追加 DL 計画用 (§4.5-2 実装帰結 4): 手元の merge 済みイベント列に
 * 見えている restore マーカー (`toSeq` 以前) の系譜土台に必要な seq 範囲を
 * 返す。境界が未取得の場合は下端 0 の保守的範囲になる (マーカー発見が DL 後に
 * なる鶏卵は「merge → 走査 → 追加 DL → re-merge」の反復で解く —
 * `strokeLogTimeline.loadStrokeLogTimeline` 参照)。
 */
export const collectLineageBaseRanges = (
  events: readonly StrokeLogEvent[],
  visible: SeqRange | undefined,
): readonly SeqRange[] => {
  const out: SeqRange[] = [];
  for (const ev of events) {
    if (ev.t !== 'whiteboard.restore') continue;
    if (visible !== undefined && ev.seq > visible.toSeq) continue; // 終端より後の文書は無関係
    collectChainRanges(events, ev, MAX_LINEAGE_DEPTH, true, out);
  }
  return out;
};

/**
 * `toSeq` 時点で**画面に残っている最終文書**を構成する範囲を適用順
 * (root→leaf、昇順・互いに素) で返す — 最終文書が restore で始まるなら
 * [系譜チェーン root→leaf, マーカーの次〜toSeq]、そうでなければ従来どおり
 * [最後の全消し (or ログ先頭)〜toSeq] の単一範囲。イベントが無ければ空。
 *
 * 用途は 2 つ:
 *   - **自動クロップ** (encodeJob): 既存のクロップ意味論 (= 最終文書の外接
 *     矩形にフィットさせ、それ以前のフレームの描画は枠外に出てもよい) を
 *     系譜に拡張したもの。
 *   - **§4.7 系譜連結ドメイン (スライダー可動域)**: restore は「壁」ではなく
 *     「接続」— 可動域 = この範囲リストを連結した仮想タイムライン。返り値を
 *     そのまま `createTimelapseFrameSource` / `TimelapseEncodeRequest` の
 *     `seqRanges` に渡せる (純粋な全消し境界のクランプは最終文書の開始 =
 *     先頭範囲の下端として維持される)。飛ばされた中間文書と各文書の非復元
 *     tail は範囲外。
 *
 * `bakedSessionIds` (省略可): 「人を外して戻す」の baked 行 (`kind: 'excluded'`)
 * のセッション集合。restore の復元元がこの集合に入るときは、そこで系譜を止める
 * (protocol `resolveLineage` と同じ規則)。省略 = 従来どおり (タイムラプス等は
 * 省略 = 挙動不変)。
 */
export const collectFinalDocumentRanges = (
  events: readonly StrokeLogEvent[],
  toSeq: number,
  bakedSessionIds?: ReadonlySet<string>,
): readonly SeqRange[] => {
  const boundary = findDocumentStart(events, toSeq);
  if (boundary === null) return [];
  if (boundary.kind === 'restore') {
    const rebuild = buildRestoreRebuild(
      events,
      boundary.marker,
      MAX_LINEAGE_DEPTH,
      bakedSessionIds,
    );
    const m = boundary.marker.seq;
    return m + 1 <= toSeq ? [...rebuild.ranges, { fromSeq: m + 1, toSeq }] : [...rebuild.ranges];
  }
  return [{ fromSeq: boundary.seq, toSeq }];
};
